import { formatRate, type RateUnit } from '@/core/display-settings'
import { widgetCopy, widgetText } from '@/i18n/widget-copy'
import type { LiveRateMetric, SessionRecord } from '@/domain/types'
import type { AppLocale } from '@/i18n/messages'

export function duration(ms: number): string {
  const seconds = Math.floor(ms / 1_000)
  const hours = Math.floor(seconds / 3_600)
  const minutes = Math.floor((seconds % 3_600) / 60)
  const remainder = seconds % 60
  return hours > 0
    ? `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainder.toString().padStart(2, '0')}`
    : `${minutes.toString().padStart(2, '0')}:${remainder.toString().padStart(2, '0')}`
}

export function percent(value: number | null | undefined, maximumFractionDigits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '--'
  const percentage = Math.min(1, Math.max(0, value)) * 100
  return `${Intl.NumberFormat(undefined, { maximumFractionDigits }).format(percentage)}%`
}

export function ratioPercent(value: number | null | undefined): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? '0%'
    : `${Math.round(Math.min(1, Math.max(0, value)) * 100)}%`
}

export function compact(value: number | null | undefined, locale: AppLocale): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? '--'
    : Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(Math.round(value))
}

export function liveRate(metric: LiveRateMetric | null | undefined, locale: AppLocale, rateUnit: RateUnit = 'hour'): string {
  if (!metric || metric.status === 'unavailable') return '--'
  return rateUnit === 'hour' ? compact(metric.value, locale) : formatRate(metric.value, locale, rateUnit)
}

export function signedCompact(value: number | null | undefined, locale: AppLocale): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '--'
  const formatted = Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(Math.round(Math.abs(value)))
  return `${value >= 0 ? '+' : '-'}${formatted}`
}

export function estimate(ms: number | null, locale: AppLocale): string {
  if (ms === null || !Number.isFinite(ms)) return '--'
  if (ms <= 0) return widgetCopy(locale, 'Ready', '可升级')
  const minutes = Math.max(1, Math.ceil(ms / 60_000))
  if (minutes < 60) return widgetText(locale, "{p0}m", "{p0}分钟", { p0: minutes })
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  if (hours < 24) return widgetText(locale, "{p0}h {p1}m", "{p0}小时{p1}分", { p0: hours, p1: remainder })
  const days = Math.floor(hours / 24)
  return widgetText(locale, "{p0}d {p1}h", "{p0}天{p1}小时", { p0: days, p1: hours % 24 })
}

export function statusText(status: SessionRecord['status'] | undefined, locale: AppLocale): string {
  return widgetCopy(locale, { idle: 'Idle', running: 'Recording', paused: 'Paused', ended: 'Ended', interrupted: 'Interrupted' },
    { idle: '空闲', running: '记录中', paused: '已暂停', ended: '已结束', interrupted: '已中断' })[status ?? 'idle']
}
